import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ButtonGroup, ToggleButton, Alert, Modal } from 'react-bootstrap';

import LoadingWrapper from "../components/LoadingWrapper";
import NCRTasksTable from "../components/NCRTasks/NCRTasksTable";
import { EventStatus } from "../enums/EventStatus";
import TaskService from '../services/TaskService'
import EmployeeDetail from "../types/Employee/EmployeeDetail";
import IEmployeeInfo from '../types/Employee/EmployeeInfo';
import ITask from '../types/Task/Task';
import ITaskEvent from '../types/Task/TaskEvent';

type Props = {
  currentUser: IEmployeeInfo;
  preloadedTasks?: ITask[];
  updateTasks?: (e: any) => void;
};

export type TTaskList = ITask[]

export default function TasksPage({ currentUser, preloadedTasks, updateTasks }: Props) {
  const [tasks, setTasks] = useState<ITask[] | undefined>(preloadedTasks);
  const [events, setEvents] = useState<ITaskEvent[] | undefined>();
  const [radioValue, setRadioValue] = useState("All");
  const [openTaskGroupModal, setOpenTaskGroupModal] = useState(false);
  const [taskGroupModalMembers, setTaskGroupModalMembers] = useState<EmployeeDetail[]>([]);

  const timerRef = useRef<number>(0);

  const getTasks = useCallback(async () => {
    if (!currentUser.email) return;
    
    await TaskService.getTasks(currentUser.email)
      .then((response) => {
        setTasks(response.data);
        if (updateTasks) {
          updateTasks(response.data);
        }
      })
      .catch(() => {
        setTasks([]);
      });
  }, [currentUser.email, updateTasks]);

  const getUserEvents = useCallback(async () => {
    if (!currentUser.email) return;

    await TaskService.getUserEvents(currentUser.email)
      .then((response) => {
        setEvents(response.data);

        clearInterval(timerRef.current);

        // Check if there are any pending or in-progress events
        if (response.data.filter((x: ITaskEvent) => x.status !== EventStatus.Fail).length > 0) {
          timerRef.current = window.setInterval(() => getUserEvents(), 5000);
        } // Check if there are any failed events
        else if (response.data.filter((x: ITaskEvent) => x.status === EventStatus.Fail).length > 0) {
          getTasks();
        }
      })
      .catch(() => {
        setEvents([]);
      });
  }, [currentUser.email, getTasks]);

  // Initial data load
  useEffect(() => {
    if (!currentUser.email) return;

    getTasks();
    getUserEvents();

    return () => {
      clearInterval(timerRef.current);
    };
  }, [currentUser.email, getTasks, getUserEvents]);

  const removeEvent = (taskID: number) => {
    // Remove event from state
    if (events) {
      setEvents(events.filter(x => x.taskID !== taskID));
    }
    
    // Call API to update dismiss flag
    TaskService.dismissUserEvents(taskID.toString(), currentUser.email);
  };

  const onOpenTaskGroupModal = (task: ITask) => {
    setTaskGroupModalMembers(task.groupUsers ? task.groupUsers : []);
    setOpenTaskGroupModal(true);
  };

  const onCloseTaskGroupModal = () => {
    setOpenTaskGroupModal(false);
    setTaskGroupModalMembers([]);
  };

  const currentUserEmail = currentUser.email;

  // Filter tasks based on Radio value
  let displayedTasks: ITask[] = [];
  if (tasks) {
    switch (radioValue) {
      case "All":
        displayedTasks = tasks;
        break;
      case "Mine":
        displayedTasks = tasks.filter(x => x.assignedTo === currentUserEmail);
        break;
      case "Claimable":
        displayedTasks = tasks.filter(x => x.assignedTo === "" && x.groupUsers?.find(u => u.employeeEmail === currentUserEmail));
        break;
      case "Viewable":
        displayedTasks = tasks.filter(x => x.assignedTo !== currentUserEmail && (x.assignedTo !== "" || !x.groupUsers?.find(u => u.employeeEmail === currentUserEmail)));
        break;
    }
  }

  const radios = [
    { name: 'All', value: 'All' },
    { name: 'Mine', value: 'Mine'},
    { name: 'Claimable', value: 'Claimable' },
    { name: 'Viewable', value: 'Viewable'}
  ];

  return (
    <div style={{width: "85%"}}>
      <LoadingWrapper showLoading={!tasks} loadingMessage="Getting Your NCR Tasks..." windSpinner windSpinnerScale={3}>
        <Modal show={openTaskGroupModal} onHide={onCloseTaskGroupModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Task Assigned To:</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <ul>
                {taskGroupModalMembers.map((user, i) => 
                    <li key={i}>{user.employeeName}</li>
                )}
              </ul>
            </div>
          </Modal.Body>
        </Modal>
        {events?.filter(x => x.status === EventStatus.Fail).map((event) => 
          <div key={event.taskID}>
          <Alert variant="danger" onClose={() => removeEvent(event.taskID)} dismissible>
            An error occurred while submitting <Link to={{pathname: `/taskdetails/${event.taskID}`} }>{event.ncrID}</Link>. Please submit the NCR again or contact IT Support.
          </Alert>
          </div>
        )}
        <h1 className="header mt-5 text-center">NCR Tasks</h1>
        <div className="d-flex justify-content-start align-items-center mb-3">
          <ButtonGroup>
            {radios.map((radio, idx) => (
              <ToggleButton
                className={radioValue === radio.value ? "toggled-button-active" : "toggled-button"}
                key={idx}
                id={`radio-${idx}`}
                type="radio"
                variant="outline-primary"
                name="radio"
                value={radio.value ?? ""}
                checked={radioValue === radio.value}
                onChange={(e) => setRadioValue(e.currentTarget.value)}
              >
                {radio.name}
              </ToggleButton>
            ))}
          </ButtonGroup>
        </div>
        
        <NCRTasksTable
          taskList={displayedTasks}
          currentUserEmail={currentUserEmail}
          currentUserJobCode={currentUser.jobCode}
          onGroupClick={onOpenTaskGroupModal}
        />
      </LoadingWrapper>
    </div>
  );
}